import type { IntakeDoneDto, IntakeEmailDto, IntakePageDto } from '@nsb/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorText, queryClient, type ApiError } from '../api.ts';
import { useFeedbackRecord } from '../components/feedback-context.ts';
import { Button, ErrorLine, LoadError, Loading, Page, TextLink, Title } from '../components/ui.tsx';
import { dateWords } from '../days.ts';

/**
 * The Intake page (spec "Intake" §5; design study 14, page A; Jason, 2026-10-06: "a place for me to say ... actually
 * this, I forwarded this thing"): what mail to the intake address could not settle by itself, waiting on NSB, then
 * what it did by itself this week, each with Undo. Linked from the Leads column on Projects with how many wait.
 */
export function Intake() {
  const { data, error } = useQuery<IntakePageDto, ApiError>({ queryKey: ['intake'], queryFn: () => api<IntakePageDto>('/intake') });
  useFeedbackRecord(null, null);
  if (error) return <LoadError error={error} />;
  if (!data) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  return (
    <Page>
      <Title kicker="Intake">Mail to intake</Title>
      <p className="max-w-measure text-ink-2">What the portal could not settle by itself, then what it did this week.</p>
      <section className="mt-10" aria-labelledby="intake-waiting">
        <h2 id="intake-waiting" className="border-b border-ink/40 pb-2 text-[1.375rem]">
          Waiting on you <span className="num text-[1rem] text-ink-3">· {data.waiting.length}</span>
        </h2>
        {data.waiting.length ? (
          <ul className="divide-y divide-rule border-b border-rule">
            {data.waiting.map((e) => (
              <Waiting key={e.id} email={e} />
            ))}
          </ul>
        ) : (
          <p className="py-4 text-ink-3">{data.blocked ?? 'Nothing waits on you.'}</p>
        )}
      </section>
      <section className="mt-12" aria-labelledby="intake-done">
        <h2 id="intake-done" className="border-b border-ink/40 pb-2 text-[1.375rem]">
          Done by itself <span className="num text-[1rem] text-ink-3">· this week</span>
        </h2>
        {data.done.length ? (
          <ul className="divide-y divide-rule border-b border-rule">
            {data.done.map((d) => (
              <Done key={d.id} item={d} />
            ))}
          </ul>
        ) : (
          <p className="py-4 text-ink-3">Nothing this week.</p>
        )}
      </section>
    </Page>
  );
}

const when = (iso: string | null) => (iso ? dateWords(iso) : null);
const fromLine = (from: string | null, at: string | null) => [from ? `From ${from.replace(/\s*<[^>]+>$/, '')}` : null, when(at)].filter(Boolean).join(', ');

async function refresh() {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['intake'] }),
    queryClient.invalidateQueries({ queryKey: ['leads', 'intake'] }),
    queryClient.invalidateQueries({ queryKey: ['pipeline'] }),
  ]);
}

/** An email waiting on NSB: what it is, in a line, and Make it a lead, Edit first or Not for the portal. */
function Waiting({ email: e }: { email: IntakeEmailDto }) {
  const [busy, setBusy] = useState<'lead' | 'discard' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const run = async (what: 'lead' | 'discard') => {
    setBusy(what);
    setErr(null);
    try {
      await api(`/intake/${e.id}/${what}`, { body: {} });
      await refresh();
    } catch (x) {
      setErr(errorText(x));
      setBusy(null);
    }
  };
  return (
    <li className="py-4">
      <p className="font-medium">{e.subject || '(no subject)'}</p>
      <p className="meta">{fromLine(e.from, e.receivedAt)}</p>
      {e.why ? <p className="mt-1 max-w-measure text-[0.9375rem] text-ink-2">{e.why}</p> : null}
      <div className="mt-2 flex flex-wrap gap-x-5">
        <Button tone="quiet" busy={busy === 'lead'} disabled={busy !== null} onClick={() => void run('lead')}>
          Make it a lead
        </Button>
        <TextLink to={`/leads/new?intake=${e.id}`} className="inline-flex min-h-11 items-center text-ink-2 hover:text-ink">
          Edit first
        </TextLink>
        <Button tone="quiet" busy={busy === 'discard'} disabled={busy !== null} onClick={() => void run('discard')}>
          Not for the portal
        </Button>
      </div>
      <ErrorLine error={err} />
    </li>
  );
}

const DONE_WORDS: Record<IntakeDoneDto['what'], string> = { made: 'Made a lead', kept: 'Kept with', set_aside: 'Not for the portal' };

/** What was done by itself: a lead made (Undo archives it), an email kept with a project, or one set aside (Undo brings it back). */
function Done({ item: d }: { item: IntakeDoneDto }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const undo = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/intake/${d.id}/undo`, { body: {} });
      await refresh();
    } catch (x) {
      setErr(errorText(x));
    } finally {
      setBusy(false);
    }
  };
  const canUndo = d.what === 'set_aside' || (d.what === 'made' && d.project && !d.project.archived);
  return (
    <li className="py-3.5">
      <div className="flex items-baseline justify-between gap-4">
        <span className="min-w-0">
          <span className="block">
            {DONE_WORDS[d.what]}
            {d.project ? (
              <>
                {d.what === 'made' ? ': ' : ' '}
                <Link to={`/p/${d.project.id}`} className="link">
                  {d.project.name}
                </Link>
                {d.project.archived ? <span className="meta"> · archived</span> : null}
              </>
            ) : null}
          </span>
          <span className="meta block">{[d.subject || '(no subject)', fromLine(d.from, d.receivedAt)].filter(Boolean).join(' · ')}</span>
        </span>
        {canUndo ? (
          <Button tone="quiet" busy={busy} onClick={() => void undo()}>
            Undo
          </Button>
        ) : null}
      </div>
      <ErrorLine error={err} />
    </li>
  );
}
